//@flow
import React, {useState, useEffect, useRef} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {Publish, PictureAsPdf, Delete} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {getStringDate} from '../../../../utils/date';
import {FileDropZone} from '../DropZone';
import {useTrainBotActions} from '../../hooks';
import {useToastActions} from '../../../toast-alert/utils';
import type {StoreChatbot, ChatbotTraining} from '../../../entities';
import {getFileUrl} from '../../../../utils/image_provider';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  documentsSection: {
    flex: '1 1 auto',
  },
  documentsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '.5rem',
    },
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '8px',
    marginBottom: '0.5rem',
    backgroundColor: colors.grey50,
  },
  documentLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  documentIcon: {
    color: colors.primary,
  },
  documentLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  documentText: {
    fontSize: '0.875rem',
    color: colors.grey800,
    fontWeight: 500,
  },
  documentSecondary: {
    fontSize: '0.75rem',
    color: colors.grey600,
  },
  deleteButton: {
    color: colors.danger,
    cursor: 'pointer',
    padding: '4px',
  },
  uploadSection: {
    flex: '0 0 auto',
  },
  uploadArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '120px',
    background: colors.grey50,
    borderRadius: '8px',
    border: `2px dashed ${colors.grey300}`,
    gap: '0.5rem',
    color: colors.grey700,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: '1rem',
    '&:hover': {
      borderColor: colors.primary,
      backgroundColor: colors.grey100,
    },
  },
  icon: {
    color: colors.primary,
    backgroundColor: colors.lightPurple,
    borderRadius: '50%',
    padding: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  caption: {
    fontSize: '0.75rem',
    color: colors.grey600,
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  submitButton: {
    padding: '0.5rem 1rem',
    maxWidth: '200px',
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    maxWidth: '200px',
  },
}));

type DocumentTraining = {
  name: string,
  createdAt: string,
  updatedAt: string,
  file?: File,
  id?: string,
  fileId?: string,
};

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
  initialTrainDocuments: Array<ChatbotTraining>,
};

const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

const TrainingTabPDF = React.memo(function TrainingTabPDF(props: Props) {
  const classes = useStyles();
  const {showToast} = useToastActions();
  const {trainBot, loading} = useTrainBotActions();

  const [trainDocuments, setTrainDocuments] = useState<DocumentTraining[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const hasSavedFilesRef = useRef(false);
  useEffect(() => {
    const docs = props.initialTrainDocuments.map(training => {
      const pdf = training.metadata.values?.PDF;
      return {
        name: pdf?.file_name,
        createdAt: training.createdAt,
        updatedAt: training.updatedAt,
        id: training.id,
        fileId: pdf?.file_id,
      };
    });
    hasSavedFilesRef.current = docs.length > 0;
    setTrainDocuments(docs);
    setHasChanges(false);
  }, [props.initialTrainDocuments]);

  const handleDeleteDocument = (index: number) => {
    setTrainDocuments(prev => {
      const newDocuments = [...prev];
      newDocuments.splice(index, 1);
      return newDocuments;
    });
    setHasChanges(true);
  };

  const handleUploadFile = files => {
    if (!files) return;

    const newDocuments = [...trainDocuments];
    let filesRejected = 0;

    Array.from(files).forEach(file => {
      if (file.size > MAX_DOCUMENT_SIZE) {
        filesRejected++;
        showToast({
          message: `El archivo ${file.name} es demasiado grande, el tamaño máximo permitido es de 10MB`,
          title: 'Archivo demasiado grande',
          type: 'warning',
          duration: 5000,
        });
      } else if (file.type !== 'application/pdf') {
        filesRejected++;
        showToast({
          message: `El archivo ${file.name} no es un PDF válido`,
          title: 'Formato no válido',
          type: 'warning',
          duration: 3000,
        });
      } else {
        const documentTraining: DocumentTraining = {
          name: file.name,
          file,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        newDocuments.push(documentTraining);
      }
    });

    if (filesRejected > 0) {
      showToast({
        message: `${filesRejected} archivo(s) no se pudieron agregar. Verifica el formato y tamaño.`,
        title: 'Algunos archivos fueron rechazados',
        type: 'warning',
        duration: 5000,
      });
    }

    setTrainDocuments(newDocuments);
    setHasChanges(true);
  };

  const getButtonText = () => {
    if (uploadingFiles) return 'Subiendo archivos...';
    if (loading) return 'Guardando...';
    return 'Guardar';
  };

  const handleCancel = () => {
    // Reset to initial state
    const docs = props.initialTrainDocuments.map(training => {
      const pdf = training.metadata.values?.PDF;
      return {
        name: pdf?.file_name,
        createdAt: training.createdAt,
        updatedAt: training.updatedAt,
        id: training.id,
        fileId: pdf?.file_id,
      };
    });
    setTrainDocuments(docs);
    setHasChanges(false);

    showToast({
      message: 'Cambios descartados',
      title: 'Cancelado',
      type: 'info',
      duration: 2000,
    });
  };

  const handleTrain = async () => {
    if (!props.chatBot?.id) return;

    try {
      const filesIds: string[] = trainDocuments
        .map(doc => doc.fileId)
        .filter(Boolean);

      const files: File[] = trainDocuments.map(doc => doc.file).filter(Boolean);

      if (
        filesIds.length === 0 &&
        files.length === 0 &&
        !hasSavedFilesRef.current
      ) {
        showToast({
          message:
            'Por favor sube al menos un documento PDF para entrenar el bot',
          title: 'Documentos requeridos',
          type: 'warning',
          duration: 3000,
        });
        return;
      }

      setUploadingFiles(true);

      await trainBot(props.chatBot.id, {
        trainingType: 'PDF',
        filesIds,
        files,
      });

      setUploadingFiles(false);

      const timeMessage =
        files.length > 3
          ? 'Este proceso puede tardar de 30 minutos a 1 hora dependiendo del tamaño y cantidad de archivos.'
          : 'Este proceso puede tardar de 15 a 30 minutos dependiendo del tamaño de los archivos.';

      showToast({
        message: `Entrenamiento con documentos iniciado. ${timeMessage}`,
        title: 'Entrenamiento iniciado',
        type: 'success',
        duration: 8000,
      });

      setHasChanges(false);
      props.onSaveSuccess();
    } catch (error) {
      setUploadingFiles(false);
      const errorMessage =
        error?.message || 'Error desconocido durante el entrenamiento';
      showToast({
        message: errorMessage,
        title: 'Error en el entrenamiento',
        type: 'danger',
        duration: 5000,
      });
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.uploadSection}>
          <input
            type="file"
            hidden
            accept=".pdf"
            onChange={e => handleUploadFile(e.target.files)}
            multiple
            id="pdf-upload"
          />
          <label
            htmlFor="pdf-upload"
            style={{cursor: 'pointer', width: '100%'}}
          >
            <FileDropZone onDrop={handleUploadFile}>
              <span className={classes.icon}>
                <Publish />
              </span>
              <span className={classes.label}>Subir PDF</span>
              <Typography
                variant="caption"
                className={classes.caption}
                align="center"
              >
                Arrastra y suelta archivos PDF aquí o haz clic para seleccionar
              </Typography>
            </FileDropZone>
          </label>
        </div>
        <div className={classes.documentsSection}>
          <ul className={classes.documentsList}>
            {trainDocuments.map((document, index) => (
              <li key={index} className={classes.documentItem}>
                <div className={classes.documentLeft}>
                  <PictureAsPdf className={classes.documentIcon} />
                  <a
                    href={getFileUrl(document.fileId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.documentLink}
                  >
                    <div className={classes.documentText}>{document.name}</div>
                    <div className={classes.documentSecondary}>
                      {getStringDate(document.updatedAt)}
                    </div>
                  </a>
                </div>
                <Delete
                  className={classes.deleteButton}
                  onClick={() => handleDeleteDocument(index)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={classes.sectionFooter}>
        <CustomButton
          variant="outlined"
          color="default"
          className={classes.cancelButton}
          onClick={handleCancel}
          disabled={loading || !hasChanges}
          fullWidth
        >
          Cancelar
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={handleTrain}
          disabled={loading || uploadingFiles}
          fullWidth
        >
          {getButtonText()}
        </CustomButton>
      </div>
    </>
  );
});

export default TrainingTabPDF;
